import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, FileText, LayoutDashboard, Pencil, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { ActionsSidebar } from "@/components/ActionsSidebar";
import { ActionDetail } from "@/components/ActionDetail";
import { ActionDialog } from "@/components/ActionDialog";
import { PatientDialog } from "@/components/PatientDialog";
import { PatientSummary } from "@/components/PatientSummary";
import { RemindersPanel } from "@/components/RemindersPanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorMessage } from "@/lib/api";
import { DOMAINS, DOMAIN_DESCRIPTIONS, ageFromBirth, domainLabel, fmtDate } from "@/lib/aba";
import { PATIENT } from "@/constants/testIds";

export default function PatientPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const domain = params.get("dominio") || "panoramica";
  const selectedActionId = params.get("azione");
  const [newActionOpen, setNewActionOpen] = useState(false);
  const [editPatient, setEditPatient] = useState(false);
  const [deletePatient, setDeletePatient] = useState(false);

  const { data: patient, isLoading: loadingPatient, isError } = useQuery({
    queryKey: ["patient", patientId],
    queryFn: async () => (await api.get(`/patients/${patientId}`)).data,
  });

  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ["summary", patientId],
    queryFn: async () => (await api.get(`/patients/${patientId}/summary`)).data,
  });

  const isDomain = domain !== "panoramica";
  const { data: actions = [], isLoading: loadingActions } = useQuery({
    queryKey: ["actions", patientId, domain],
    queryFn: async () => (await api.get(`/patients/${patientId}/actions`, { params: { domain } })).data,
    enabled: isDomain,
  });

  // Auto-select first action when entering a domain without selection
  useEffect(() => {
    if (!isDomain || loadingActions) return;
    if (actions.length && (!selectedActionId || !actions.some((a) => a.action_id === selectedActionId))) {
      setParams((p) => { p.set("azione", actions[0].action_id); return p; }, { replace: true });
    }
    if (!actions.length && selectedActionId) {
      setParams((p) => { p.delete("azione"); return p; }, { replace: true });
    }
  }, [isDomain, loadingActions, actions, selectedActionId, setParams]);

  const changeDomain = (key) => {
    setParams((p) => { p.set("dominio", key); p.delete("azione"); return p; });
  };
  const selectAction = (id) => setParams((p) => { p.set("azione", id); return p; });

  const removePatient = useMutation({
    mutationFn: async () => (await api.delete(`/patients/${patientId}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["patients"] }); toast.success("Paziente eliminato"); navigate("/dashboard", { replace: true }); },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const domainCounts = useMemo(() => Object.fromEntries((summary?.domains || []).map((d) => [d.key, d])), [summary]);
  const age = ageFromBirth(patient?.data_nascita);

  if (isError) {
    return (
      <AppShell breadcrumbs={[{ label: "Pazienti", to: "/dashboard" }, { label: "Non trovato" }]}>
        <Card><CardContent className="py-16 text-center"><p className="text-destructive">Paziente non trovato.</p><Button variant="link" onClick={() => navigate("/dashboard")}>Torna alla lista</Button></CardContent></Card>
      </AppShell>
    );
  }

  return (
    <AppShell breadcrumbs={[{ label: "Pazienti", to: "/dashboard" }, { label: patient ? `${patient.nome} ${patient.cognome}`.trim() : "…" }]}>
      {/* Patient header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {loadingPatient ? (
          <Skeleton className="h-16 w-72" />
        ) : (
          <div className="flex items-center gap-4">
            <span className="h-14 w-14 rounded-2xl bg-accent text-primary flex items-center justify-center shrink-0"><UserRound className="h-7 w-7" /></span>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="patient-name">{patient.nome} {patient.cognome}</h1>
              <p className="text-sm text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                {age !== null && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {age} anni · nato il {fmtDate(patient.data_nascita)}</span>}
                {patient.diagnosi && <span>{patient.diagnosi}</span>}
                {patient.note && <span className="italic truncate max-w-[360px]" title={patient.note}>{patient.note}</span>}
              </p>
            </div>
          </div>
        )}
        <div className="flex gap-2 no-print flex-wrap">
          <Button className="gap-2" onClick={() => navigate(`/pazienti/${patientId}/report`)} data-testid="patient-report-button"><FileText className="h-4 w-4" /> Report PDF</Button>
          <Button variant="outline" className="gap-2" onClick={() => setEditPatient(true)} data-testid={PATIENT.editButton}><Pencil className="h-4 w-4" /> Modifica</Button>
          <Button variant="outline" className="gap-2 text-destructive hover:text-destructive" onClick={() => setDeletePatient(true)} data-testid={PATIENT.deleteButton}><Trash2 className="h-4 w-4" /> Elimina</Button>
        </div>
      </div>

      {/* Domain tabs */}
      <Tabs value={domain} onValueChange={changeDomain} className="mb-5">
        <TabsList className="h-auto flex-wrap justify-start bg-card border border-border rounded-xl p-1 gap-1" data-testid={PATIENT.domainTabs}>
          <TabsTrigger value="panoramica" className="gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid={PATIENT.summaryTab}><LayoutDashboard className="h-4 w-4" /> Panoramica</TabsTrigger>
          {DOMAINS.map((d) => {
            const c = domainCounts[d.key];
            return (
              <TabsTrigger key={d.key} value={d.key} className="gap-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid={`domain-tab-${d.key}`}>
                {d.label}
                {c?.azioni > 0 && <span className="text-[10px] rounded-full bg-muted text-muted-foreground px-1.5 min-w-[18px] text-center data-[state=active]:bg-white/20">{c.azioni}</span>}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {!isDomain ? (
        <div className="space-y-6">
          <RemindersPanel patientId={patientId} />
          <PatientSummary summary={summary} isLoading={loadingSummary} onOpenDomain={changeDomain} />
        </div>
      ) : (
        <div>
          <p className="text-sm text-muted-foreground mb-4">{DOMAIN_DESCRIPTIONS[domain]}</p>
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 items-start">
            <ActionsSidebar actions={actions} isLoading={loadingActions} selectedId={selectedActionId} onSelect={selectAction} onNew={() => setNewActionOpen(true)} domainLabel={domainLabel(domain)} />
            <div className="min-w-0">
              {selectedActionId && actions.some((a) => a.action_id === selectedActionId) ? (
                <ActionDetail actionId={selectedActionId} patientId={patientId} onDeleted={() => setParams((p) => { p.delete("azione"); return p; })} />
              ) : loadingActions ? (
                <Skeleton className="h-80 rounded-xl" />
              ) : (
                <Card className="border-dashed">
                  <CardContent className="py-20 flex flex-col items-center text-center gap-3" data-testid="domain-empty-state">
                    <h3 className="font-heading text-xl font-semibold">Nessuna azione in {domainLabel(domain)}</h3>
                    <p className="text-muted-foreground max-w-md">Crea un'azione (es. “Batti le mani”) e descrivi cosa deve compiere il bambino. Ogni azione avrà il proprio grafico ABA.</p>
                    <Button className="mt-2" onClick={() => setNewActionOpen(true)} data-testid="domain-empty-new-action">Crea la prima azione</Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}

      <ActionDialog open={newActionOpen} onOpenChange={setNewActionOpen} patientId={patientId} domain={domain} onSaved={(a) => selectAction(a.action_id)} />
      <PatientDialog open={editPatient} onOpenChange={setEditPatient} patient={patient} />
      <AlertDialog open={deletePatient} onOpenChange={setDeletePatient}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il paziente?</AlertDialogTitle>
            <AlertDialogDescription>Verranno eliminate tutte le azioni e le sessioni di {patient?.nome}. L'operazione non è reversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={(e) => { e.preventDefault(); removePatient.mutate(); }} data-testid="patient-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
